import { configureStore, combineReducers } from '@reduxjs/toolkit';
import langReducer from '../lang/langSlice';
import appReducer from '../../features/app/appSlice';
import loginReducer from '../login/loginSlice';
import tokenHandlingReducer from '../tokenHandling/tokenHandlingSlice';
import tagReducer from '../../features/app/tags/tagsSlice';
import { userProfileObtained } from '../login/loginSlice';
import type { UnknownAction } from '@reduxjs/toolkit';
import tagCreationDialogReducer from '../../features/app/tags/create/tagCreationDialogSlice';
import tagListToolbarReducer from '../../features/app/tags/list/toolbar/tagListToolbarSlice';

const combinedReducer = combineReducers({
    lang: langReducer,
    app: appReducer,
    login: loginReducer,
    tokenHandling: tokenHandlingReducer,
    tags: tagReducer,
    tagCreationDialog: tagCreationDialogReducer,
    tagsTableToolbar: tagListToolbarReducer,
});

// Reset account-owned UI/data together, and discard completions from superseded requests.
export const rootReducer = (
    previousState: ReturnType<typeof combinedReducer> | undefined,
    action: UnknownAction
): ReturnType<typeof combinedReducer> => {
    let state = previousState;
    if (state && userProfileObtained.match(action) && state.login.userProfile?.account !== action.payload.account) {
        state = {
            ...state,
            tags: tagReducer(undefined, { type: 'reset' }),
            tagCreationDialog: tagCreationDialogReducer(undefined, { type: 'reset' }),
            tagsTableToolbar: tagListToolbarReducer(undefined, { type: 'reset' }),
        };
    }
    const meta = action.meta as { requestId?: string } | undefined;
    if (
        state &&
        meta?.requestId &&
        /\/(fulfilled|rejected)$/.test(action.type) &&
        (action.type.startsWith('tags/') || action.type.startsWith('tagCreationDialog/')) &&
        state.tags.requests[action.type.replace(/\/(fulfilled|rejected)$/, '')] !== meta.requestId
    ) {
        return state;
    }
    return combinedReducer(state, action);
};

export const setupStore = (preloadedState?: Partial<RootState>) =>
    configureStore({
        reducer: rootReducer,
        preloadedState: preloadedState ? combinedReducer(preloadedState, { type: '@@init' }) : undefined,
    });

export const store = setupStore();

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];
